import { ReactNode } from 'react';
import { Link, UseStylesProps } from 'types';
import SimpleSelect, { SimpleSelectOptionType } from 'components/inputs/SimpleSelect/SimpleSelect';
import { ADMIN_LINK_CHANNEL_KEY } from 'utils/link';
import { useSelectedLinkContext, useTourContext, useWebUserContext } from 'contexts';
import ChannelLinkLabel from './ChannelLinkLabel';
import useStyles from './ChannelLinkSelect.styles';
import miniTablet from 'assets/images/tablets/mini_tablet.png';
import Image from 'components/medias/Image';
import { FormattedMessage } from 'react-intl';
import { trackEvent, GACommonEventAction, GAEventCategory } from 'services/googleAnalytics';

type ChannelLinkSelectPureProps = UseStylesProps & {
  options: SimpleSelectOptionType<Link>[];
  value: string;
  handleChange: Function;
};

const ChannelLinkSelectPure = ({
  classes,
  options,
  value,
  handleChange,
}: ChannelLinkSelectPureProps): JSX.Element => (
  <>
    <Image
      alt="Tablette Ardoiz"
      className={classes.tabletImage}
      src={miniTablet}
    />
    <SimpleSelect
      className={classes.select}
      options={options}
      value={value}
      handleChange={handleChange}
    />
  </>
);

const getLinkLabel = (link: Link): ReactNode => {
  const baseLabel = (
    <FormattedMessage
      id="channelLink.tabletOwner"
      values={{
        firstName: link?.mainUser?.firstName,
        lastName: link?.mainUser?.lastName,
      }}
    />
  );

  if (!link.metadata.subscriptionEnabled) {
    return (
      <>
        {baseLabel} (<FormattedMessage id="channelLink.subscriptionDisabled" />)
      </>
    );
  }

  return baseLabel;
};

const ChannelLinkSelect = (): JSX.Element | null => {
  const { webUser } = useWebUserContext();
  const { selectedLink, handleSelectedLinkChange } = useSelectedLinkContext();
  const { isTourOpen } = useTourContext();
  const classes = useStyles();

  const webUserLinks = webUser.links.filter(
    (link) => link.channelKey !== ADMIN_LINK_CHANNEL_KEY,
  );

  if (isTourOpen) {
    return null;
  }

  if (webUserLinks.length === 0 || !selectedLink) {
    return (
      <ChannelLinkLabel
        label={
          <span className={classes.noWebUserLink}>
            <FormattedMessage id="channelLink.noWebUserLink" />
          </span>
        }
      />
    );
  }

  if (webUserLinks.length === 1) {
    return <ChannelLinkLabel label={getLinkLabel(selectedLink)} />;
  }

  const options = webUserLinks.map((link) => ({
    disabled: !link.metadata.subscriptionEnabled,
    fullValue: link,
    label: getLinkLabel(link),
    value: link.channelKey,
  }));

  const handleChange = (val: Link): void => {
    trackEvent(
      GAEventCategory.CHANNEL_LINK,
      GACommonEventAction.SELECT,
      'Sélectionner une tablette',
    );
    handleSelectedLinkChange({ newSelectedLink: val });
  };

  return (
    <ChannelLinkSelectPure
      classes={classes}
      options={options}
      value={selectedLink.channelKey}
      handleChange={handleChange}
    />
  );
};

export default ChannelLinkSelect;
